// src/pages/Acess/Login.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth-context";

/**
 * Tela estilo rastreamento (inspirada nos Correios): código + captcha
 *
 * Ajuste estas URLs para o seu backend:
 */
const CAPTCHA_REFRESH_URL = "/api/rastreamento/captcha/refresh";
const CAPTCHA_IMAGE_URL = "/api/rastreamento/captcha/image";
const CAPTCHA_AUDIO_URL = "/api/rastreamento/captcha/audio";
const TRACKING_SUBMIT_URL = "/api/rastreamento/consultar";

const ALLOW_CPF_CNPJ = true;

function onlyDigits(s: string) {
  return s.replace(/\D+/g, "");
}

function isValidTrackingCodeOrCpfCnpj(input: string) {
  const value = input.trim();
  if (!value) return false;

  // CPF/CNPJ
  if (ALLOW_CPF_CNPJ) {
    const digits = onlyDigits(value);
    if (digits.length === 11 || digits.length === 14) return true;
  }

  // Código rastreio típico: AA123456785BR
  const code = value.replace(/\s+/g, "").toUpperCase();
  if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(code)) return true;

  // fallback: alfanumérico simples (sem caracteres estranhos)
  if (/^[A-Za-z0-9-]{5,30}$/.test(value)) return true;

  return false;
}

function useIsMobile(breakpoint: number = 900): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(mq.matches);
    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }

    // suporte legado
    // @ts-ignore
    mq.addListener(update);
    // @ts-ignore
    return () => mq.removeListener(update);
  }, [breakpoint]);

  return isMobile;
}

type CaptchaState = {
  captchaId?: string;
  imageSrc?: string; // dataURL base64 opcional
  ts?: number; // cache-bust
};

const COLORS = {
  greenDark: "#1d2708",
  blueLink: "#1b66c9",
  border: "#e5e5e5",
  border2: "#c9c9c9",
  panel: "#efefef",
  text: "#111",
};

const pageBg: React.CSSProperties = {
  minHeight: "100vh",
  background: "#ffffff",
  color: COLORS.text,
  fontFamily: "Arial, Helvetica, sans-serif",
};

const topBar: React.CSSProperties = {
  height: 64,
  borderBottom: `1px solid ${COLORS.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 18px",
};

const leftTop: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const iconButtonBase: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 0,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const logoWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const logoText: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  color: COLORS.greenDark,
  letterSpacing: 0.5,
};

const rightTop: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const enterBtn: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  padding: "10px 14px",
  borderRadius: 0,
  cursor: "pointer",
  fontWeight: 700,
  color: COLORS.greenDark,
};

const container: React.CSSProperties = {
  maxWidth: 1100,
  margin: "0 auto",
  padding: "22px 18px 60px",
};

const breadcrumb: React.CSSProperties = {
  color: "#000",
  fontSize: 14,
  marginBottom: 10,
};

const title: React.CSSProperties = {
  fontSize: 40,
  fontWeight: 800,
  margin: "8px 0 16px",
};

const panel: React.CSSProperties = {
  background: COLORS.panel,
  borderRadius: 0,
  padding: 22,
};

const question: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 300,
  marginBottom: 12,
};

const hint: React.CSSProperties = {
  fontSize: 14,
  color: "#444",
  marginTop: 6,
  marginBottom: 14,
};

const inputLine: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  marginBottom: 16,
};

const textInput: React.CSSProperties = {
  height: 52,
  borderRadius: 0,
  border: `1px solid ${COLORS.border2}`,
  padding: "0 14px",
  fontSize: 18,
  outline: "none",
  background: "#fff",
};

const gridDesktop: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1.2fr",
  gap: 18,
  alignItems: "center",
};

const captchaBox: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const captchaImgWrap: React.CSSProperties = {
  width: "100%",
  maxWidth: 420,
  height: 150,
  background: "#fff",
  border: `1px solid ${COLORS.border2}`,
  borderRadius: 0,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const captchaTools: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const toolBtn: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 0,
  border: `1px solid ${COLORS.border2}`,
  background: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const rightCol: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const consultBtn: React.CSSProperties = {
  marginTop: 10,
  width: 160,
  height: 48,
  borderRadius: 0,
  border: "none",
  background: "#526032",
  color: "#fff",
  fontSize: 18,
  fontWeight: 300,
  cursor: "pointer",
  alignSelf: "center",
};

const feedbackStyle = (type: "error" | "success"): React.CSSProperties => ({
  marginTop: 14,
  padding: "12px 14px",
  borderRadius: 0,
  border: `1px solid ${type === "error" ? "#f2b8b5" : "#b8e6c3"}`,
  background: type === "error" ? "#fff4f4" : "#f2fff6",
  color: type === "error" ? "#a40000" : "#0b6a2b",
  fontWeight: 700,
});

// SVGs (sem emoji)
const IconMenu = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M4 6h16M4 12h16M4 18h16"
      fill="none"
      stroke={COLORS.greenDark}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconEnter = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M10 17l5-5-5-5"
      fill="none"
      stroke={COLORS.greenDark}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 12h10"
      fill="none"
      stroke={COLORS.greenDark}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M14 4h6v16h-6"
      fill="none"
      stroke={COLORS.greenDark}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconRefresh = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20 12a8 8 0 0 1-14.9 3M4 12a8 8 0 0 1 14.9-3"
      fill="none"
      stroke={COLORS.blueLink}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M19 5v4h-4"
      fill="none"
      stroke={COLORS.blueLink}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 19v-4h4"
      fill="none"
      stroke={COLORS.blueLink}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconAudio = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M11 5L6 9H3v6h3l5 4V5z"
      fill="none"
      stroke={COLORS.blueLink}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.5 8.5a4 4 0 0 1 0 7"
      fill="none"
      stroke={COLORS.blueLink}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M17.8 6.2a7 7 0 0 1 0 11.6"
      fill="none"
      stroke={COLORS.blueLink}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const TrackingForm: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [codigo, setCodigo] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "error" | "success";
    msg: string;
  } | null>(null);

  const [captcha, setCaptcha] = useState<CaptchaState>({ ts: Date.now() });

  const grid: React.CSSProperties = useMemo(() => {
    if (!isMobile) return gridDesktop;
    return {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 14,
      alignItems: "start",
    };
  }, [isMobile]);

  const loadCaptcha = useCallback(async () => {
    try {
      setFeedback(null);

      // Opção 1: refresh retorna JSON: { captchaId, imageBase64 }
      const res = await fetch(CAPTCHA_REFRESH_URL, { method: "POST" });
      const data = await res.json().catch(() => null);

      if (res.ok && data && (data.imageBase64 || data.imageSrc)) {
        setCaptcha({
          captchaId: data.captchaId,
          imageSrc: data.imageBase64 || data.imageSrc,
          ts: Date.now(),
        });
        return;
      }

      // Opção 2: só cache-bust do endpoint de imagem
      setCaptcha((c) => ({ ...c, ts: Date.now() }));
    } catch {
      setCaptcha((c) => ({ ...c, ts: Date.now() }));
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const captchaImgSrc = useMemo(() => {
    if (captcha.imageSrc) return captcha.imageSrc;
    const ts = captcha.ts || Date.now();
    return `${CAPTCHA_IMAGE_URL}?t=${ts}`;
  }, [captcha.imageSrc, captcha.ts]);

  const playAudio = useCallback(async () => {
    try {
      setFeedback(null);
      const audio = new Audio(`${CAPTCHA_AUDIO_URL}?t=${Date.now()}`);
      await audio.play();
    } catch {
      setFeedback({
        type: "error",
        msg: "Não foi possível reproduzir o áudio do captcha.",
      });
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (loading) return;
    setFeedback(null);

    const codigoTrim = codigo.trim();
    const captchaTrim = captchaText.trim();

    if (!isValidTrackingCodeOrCpfCnpj(codigoTrim)) {
      setFeedback({
        type: "error",
        msg: "Digite um código válido (rastreio ou CPF/CNPJ).",
      });
      return;
    }
    if (!captchaTrim) {
      setFeedback({ type: "error", msg: "Digite o texto contido na imagem." });
      return;
    }

    try {
      setLoading(true);

      const payload: any = {
        codigo: codigoTrim,
        captchaText: captchaTrim,
      };
      if (captcha.captchaId) payload.captchaId = captcha.captchaId;

      const res = await fetch(TRACKING_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.ok !== true) {
        const msg =
          data?.erro ||
          (res.status === 401
            ? "Captcha inválido."
            : "Não foi possível consultar agora.");
        setFeedback({ type: "error", msg });

        await loadCaptcha();
        setCaptchaText("");
        return;
      }

      setFeedback({ type: "success", msg: "Consulta realizada com sucesso." });

      // Se você tiver uma rota de resultado, pode navegar:
      // navigate("/rastreamento/resultado", { state: { codigo: codigoTrim, resultado: data } });

      return;
    } catch {
      setFeedback({ type: "error", msg: "Falha de conexão. Tente novamente." });
    } finally {
      setLoading(false);
    }
  }, [codigo, captchaText, captcha.captchaId, loading, loadCaptcha]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div style={pageBg}>
      {/* Topbar */}
      <div style={topBar}>
        <div style={leftTop}>
          <button style={iconButtonBase} aria-label="Menu" type="button">
            <IconMenu />
          </button>

          <div style={logoWrap}>
            <div style={logoText}>HAVOX</div>
          </div>
        </div>

        <div style={rightTop}>
          <button
            style={enterBtn}
            onClick={() => navigate("/entrar")}
            type="button"
            title="Entrar"
          >
            <IconEnter />
            Entrar
          </button>
        </div>
      </div>

      <div style={container}>
        <div style={breadcrumb}>
          Portal HAVOX &nbsp;›&nbsp; Rastreamento &nbsp;›
        </div>

        <div style={title}>Rastreamento</div>

        <div style={panel}>
          <div style={question}>
            Deseja acompanhar seu serviço?
            <br />
            Digite o{" "}
            {ALLOW_CPF_CNPJ ? "Case do serviço-Código do Cliente" : "código"}.
          </div>

          <div style={inputLine}>
            <input
              style={textInput}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="HVX-26-001-543785"
              autoComplete="off"
              inputMode="text"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
 * Página (mantém o guard de sessão igual ao seu Login antigo)
 * ============================================================ */
const AcessLogin: React.FC = () => {
  const { sessao, loading } = useAuth();

  if (loading) return null;

  // Se já tem sessão e você NÃO quer que usuário logado use rastreamento:
  if (sessao) {
    return <Navigate to="/suprimentos/produtos" replace />;
  }

  return <TrackingForm />;
};

export default AcessLogin;
